// show client's own primers and client picked lab primers first
// don't allow editing the name of a lab primer
// for client primers do not show dropdown that contains lab primers
// 2 buttons, one to add custom/client primer, another to add a new lab primer
// reset resets to absolute original

import { useState, useEffect } from 'react';
import * as Yup from 'yup';
import {
  Table,
  TableHead,
  FormControl,
  TableBody,
  TextField,
  Paper,
  TableRow,
  TableCell,
  TableContainer,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Pagination,
  Select,
  MenuItem,
} from '@mui/material';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  Formik,
  FieldArray,
  useField,
  Field,
  useFormikContext,
  setNestedObjectValues,
} from 'formik';
import Color from './Color';
import { DebounceInput } from 'react-debounce-input';
import SangerLabPrimerPopup from '../SampleList/SangerForm/SangerLabPrimerPopup';
import { stringToColour, deterministicallyRearrange, ColorLuminance } from '../Reusable/Color';

const validationSchema = Yup.object({
  primers: Yup.array().of(
    Yup.object({
      id: Yup.string(),
      name: Yup.string().required('Required').nullable(),
      // when not lab primer need volume.
      volume: Yup.number()
        .integer()
        .when('sampleMap', {
          is: true,
          then: (schema) => schema.required('Required'),
        }),
    })
  ),
});

const PrimerTable = ({ primerActions, colorMapActions, orders }) => {
  const errorStyle = {
    color: 'red',
    marginTop: '5px',
  };

  const ErrorMessage = ({ name }) => {
    const [, meta] = useField(name);
    return meta.touched && meta.error ? (
      <Box sx={errorStyle}>{meta.error}</Box>
    ) : null;
  };

  // used to validate as soon as a new primer is added to show errors
  const FormObserver = () => {
    const { values, initialValues, validateForm, setTouched } =
      useFormikContext();

    useEffect(() => {
      (async () => {
        const validationErrors = await validateForm();
        if (Object.keys(validationErrors).length > 0) {
          setTouched(setNestedObjectValues(validationErrors, true));
        }
      })();
    }, [values, initialValues]);

    return null;
  };

  const primerSet = new Map();
    primerActions
      .getPrimers()
      .map((primer) => primerSet.set(primer.number, true))
    
  const [order, setOrder] = useState('');
  useEffect(() => {
    if (orders && orders[0]) {
      setOrder(orders[0]._id);
    }
  }, [orders]);

  return (
    <Box style={{ maxWidth: '75vw', overflowX: 'scroll', maxHeight: '100vh' }}>
      <Formik
        initialValues={{
          primers: primerActions.getPrimers(),
        }}
        validationSchema={validationSchema}
        enableReinitialize={true}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <FormObserver />
            <TableContainer component={Paper}>
              <FormControl fullWidth margin="normal">
                <FieldArray
                  name="primers"
                  validateOnChange
                  render={(arrayHelpers) => (
                    <>
                      <Table size='small'>
                        <TableHead>
                          <TableRow>
                            <TableCell align="center">Color</TableCell>
                            <TableCell align="center">Number</TableCell>
                            <TableCell align="center">Primer Name</TableCell>
                            <TableCell align="center">Volume</TableCell>
                            <TableCell align="center">Delete</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {formik.values.primers
                            ? formik.values.primers.map((primer, index) => (
                                <TableRow key={index}>
                                  <Color
                                    primer={primer}
                                    color={ColorLuminance(stringToColour((deterministicallyRearrange(primer._id))), 0.5)}
                                  />
                                  <TableCell>
                                    <p
                                      style={{
                                        marginBottom: '0',
                                        textAlign: 'center',
                                      }}
                                    >
                                      {primer.number
                                        ? primer.number
                                        : primer.order}
                                    </p>
                                  </TableCell>
                                  <TableCell>
                                    <DebounceInput
                                      debounceTimeout={500}
                                      minLength={1}
                                      element={TextField}
                                      required
                                      {...formik.getFieldProps(
                                        `primers[${index}].name`
                                      )}
                                      variant="outlined"
                                      label="Name"
                                      fullWidth
                                      onBlur={(e) => {
                                        primerActions.updateName(
                                          primer._id,
                                          e.target.value
                                        );
                                        formik.handleBlur(e);
                                      }}
                                      size="small"
                                      margin="none"
                                      disabled={primer.number !== undefined}
                                    />
                                    <ErrorMessage
                                      name={`primers[${index}].name`}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <DebounceInput
                                      debounceTimeout={500}
                                      minLength={1}
                                      element={TextField}
                                      required
                                      {...formik.getFieldProps(
                                        `primers[${index}].volume`
                                      )}
                                      {...(primer.volume !== undefined
                                        ? { type: 'number' }
                                        : { value: '∞' })}
                                      label="Volume"
                                      fullWidth
                                      disabled={primer.number !== undefined}
                                      onBlur={(e) => {
                                        primerActions.updateVolume(
                                          primer._id,
                                          e.target.value
                                        );
                                        formik.handleBlur(e);
                                      }}
                                      size="small"
                                      margin="none"
                                    />
                                    <ErrorMessage
                                      name={`primers[${index}].volume`}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Button
                                      className="remove-event"
                                      onClick={() => {
                                        primerActions.deletePrimer(primer._id);
                                      }}
                                      disabled={!primer.customDeclare}
                                    >
                                      <RemoveCircleOutlineIcon />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))
                            : null}
                        </TableBody>
                      </Table>
                      <Box display="flex" flexDirection="row" marginTop="20px">
                        <Box
                          display="flex"
                          flexDirection="row"
                          width="50%"
                          justifyContent="center"
                        >
                          <SangerLabPrimerPopup
                            addNewLabPrimer={primerActions.addLabPrimer}
                            pickedLabPrimersMap={primerSet}/>
                        </Box>
                        <Box
                          display="flex"
                          flexDirection="row"
                          width="50%"
                          justifyContent="center"
                        >
                          Order to add primer to:
                          {
                            orders && orders.length !== 0 ?
                              <Select
                                  value={order}
                                  onChange={(event) => {
                                      setOrder(event.target.value)
                                  }}
                              >
                                  {orders.map((order, index) => {
                                      return (
                                          <MenuItem value={order._id} key={index}>{order.orderCode}</MenuItem>
                                      );
                                  })} 
                                </Select> 
                                  : <></>
                          }                        
                          <Button
                            sx={{ width: '90%' }}
                            className="add-primer"
                            variant="outlined"
                            onClick={() => primerActions.createPrimer(order)}
                          >
                            Add Client Primer
                          </Button>
                        </Box>
                      </Box>
                    </>
                  )}
                />
                {typeof formik.errors.tournamentEvents === 'string' ? (
                  <div style={{ color: 'red', marginBottom: '20px' }}>
                    {formik.errors.tournamentEvents}
                  </div>
                ) : (
                  <div style={{ color: 'red', marginBottom: '20px' }} />
                )}
              </FormControl>
            </TableContainer>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default PrimerTable;
